export type Rgb = [number, number, number];

/**
 * Liest einen Hex-Farbwert tolerant ein: `#RGB`, `#RRGGBB` und `#RRGGBBAA` (Alpha wird
 * ignoriert), jeweils auch ohne `#` und mit umgebendem Leerraum. Alles andere – CSS-Farbnamen,
 * `rgb(...)`, leere oder abgeschnittene Werte – ergibt `null`.
 *
 * Bewusst streng im Rückgabewert: Aufrufer sollen einen unlesbaren Wert erkennen können, statt
 * mit einer still verrechneten Ersatzfarbe weiterzuarbeiten.
 */
/** Räumt einen Hex-Wert auf sechs Stellen ohne `#` auf (Alpha entfällt); `null`, wenn unlesbar. */
const toSixDigitHex = (color: string): string | null => {
  const hex = color.trim().replace(/^#/, '');
  if (!/^(?:[0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(hex)) return null;
  return hex.length === 3
    ? hex
        .split('')
        .map((char) => char + char)
        .join('')
    : hex.slice(0, 6);
};

export const parseHexColor = (color: string): Rgb | null => {
  const hex = toSixDigitHex(color);
  if (hex === null) return null;
  return [0, 2, 4].map((index) => Number.parseInt(hex.slice(index, index + 2), 16)) as Rgb;
};

export const rgbToHex = (rgb: Rgb): string => `#${rgb.map((value) => value.toString(16).padStart(2, '0')).join('')}`;

/**
 * Bringt einen Hex-Farbwert auf die Form `#rrggbb`; liefert `null`, wenn er nicht lesbar ist.
 * Gedacht für die Ausgabe nach CSS: ein Hex ohne führendes `#` ist dort ungültig und würde die
 * ganze Deklaration verwerfen.
 *
 * Erhält die Schreibweise des Eingabewerts – für CSS ist sie belanglos, und ein unnötiges
 * Kleinschreiben würde bestehende Ausgaben ohne Gewinn verändern.
 */
export const normalizeHexColor = (color: string): string | null => {
  const hex = toSixDigitHex(color);
  return hex === null ? null : `#${hex}`;
};
